// WA — public JS API for MCP-inserted HTML/JS (reduced surface for legacy themes).
// Full API lives in the newgen family; here unavailable sections (catalog, user,
// http, ui.mount) warn and return a rejected Promise.
import cd from '../react/cart/models/cart'
import singletons from '../react/shared/singletons'
import { sharedData } from '../models/shared_data'

const WA_VERSION = '1'

function notSupported(name) {
  return function() {
    console.warn('WA.' + name + ' is not supported in this theme')
    return Promise.reject(new Error('WA.' + name + ' is not supported in this theme'))
  }
}

function cartSnapshot() {
  const raw = cd.data || {}
  const rawItems = raw.items || []
  const items = rawItems.map((it) => ({
    itemId: it.item_id,
    quantity: it.quantity,
    price: it.price,
    priceGroupId: it.price_group_id || null,
    raw: it
  }))
  return {
    items: items,
    count: raw.count || items.length,
    totalQuantity: rawItems.length > 0 ? cd.cartTotalItems() : 0,
    totalSum: rawItems.length > 0 ? cd.sum() : 0,
    raw: raw
  }
}

// cartData does not return promises — resolve on its next subject emission.
// On a network/server error cd's .end callback throws before emitting, so the
// subject never fires; the timeout rejects instead of hanging forever (and
// unsubscribes, so failed calls do not leak subscriptions).
const CART_CHANGE_TIMEOUT_MS = 15000

function nextCartChange() {
  return new Promise((resolve, reject) => {
    let timer = null
    const sub = cd.subject.subscribe(() => {
      clearTimeout(timer)
      sub.unsubscribe()
      resolve(cartSnapshot())
    })
    timer = setTimeout(() => {
      sub.unsubscribe()
      reject(new Error('WA.cart: no cart update within ' + CART_CHANGE_TIMEOUT_MS + 'ms (request failed?)'))
    }, CART_CHANGE_TIMEOUT_MS)
  })
}

function eachNode(selector, fn) {
  const nodes = document.querySelectorAll(selector)
  for (let i = 0; i < nodes.length; i++) fn(nodes[i])
}

function mobileMenuToggle() {
  // same DOM as initMobileMenuToggle in initializers/main_page.js
  const menu = document.getElementById('mobile-menu')
  if (!menu) return
  menu.classList.toggle('active')
  document.body.classList.toggle('overflowed')
  eachNode('.mobile-menu-button', (b) => b.classList.toggle('is-active'))
}

function mobileMenuSet(open) {
  const menu = document.getElementById('mobile-menu')
  if (!menu) return
  if (menu.classList.contains('active') === open) return
  mobileMenuToggle()
}

// lookup by attribute iteration, not by selector interpolation — an arbitrary
// id must not need querySelector escaping (same approach as the newgen WA)
function findWaStyle(id) {
  const styles = document.head.querySelectorAll('style[data-wa-style]')
  for (let i = 0; i < styles.length; i++) {
    if (styles[i].getAttribute('data-wa-style') === String(id)) return styles[i]
  }
  return null
}

function setStyle(id, css) {
  let el = findWaStyle(id)
  if (!el) {
    el = document.createElement('style')
    el.setAttribute('data-wa-style', String(id))
    document.head.appendChild(el)
  }
  el.textContent = css == null ? '' : String(css)
}

function removeStyle(id) {
  const el = findWaStyle(id)
  if (el && el.parentNode) el.parentNode.removeChild(el)
}

function setCatalogCols(n) {
  // legacy themes normally have no newgen catalog grid — then this is a no-op
  n = Math.max(2, Math.min(6, parseInt(n, 10) || 0))
  const containers = []
  eachNode('.ngg-catalog-grid', (node) => containers.push([node, 'ngg-catalog-grid--cols-']))
  eachNode('.row', (node) => {
    if (node.querySelector('.item-thumb')) containers.push([node, 'ng-catalog-cols-'])
  })
  containers.forEach(([node, prefix]) => {
    const drop = []
    for (let i = 0; i < node.classList.length; i++) {
      const cls = node.classList[i]
      if (cls.indexOf('ng-catalog-cols-') === 0 || cls.indexOf('ngg-catalog-grid--cols-') === 0) drop.push(cls)
    }
    drop.forEach((cls) => node.classList.remove(cls))
    node.classList.add(prefix + n)
  })
}

export default function installWA() {
  let isReady = false
  const readyQueue = []
  const listeners = {}

  function on(event, cb) {
    if (typeof cb !== 'function') return function() {}
    ;(listeners[event] = listeners[event] || []).push(cb)
    if (event === 'ready' && isReady) setTimeout(() => cb(WA), 0)
    return function() { off(event, cb) }
  }

  function off(event, cb) {
    const list = listeners[event]
    if (!list) return
    const idx = list.indexOf(cb)
    if (idx > -1) list.splice(idx, 1)
  }

  function emit(event, payload) {
    ;(listeners[event] || []).slice().forEach((cb) => {
      try { cb(payload) } catch (e) { console.error('WA listener for "' + event + '" failed', e) }
    })
  }

  function documentEvent(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: detail }))
    } catch (e) {
      console.error(e)
    }
  }

  function cartSet(itemId, qty, opts) {
    const promise = nextCartChange()
    try {
      cd.updateItem({ itemId: itemId, priceGroupId: opts && opts.priceGroupId }, qty)
    } catch (e) {
      return Promise.reject(e)
    }
    return promise
  }

  const WA = {
    version: WA_VERSION,

    ready: function(cb) {
      if (typeof cb !== 'function') return
      if (isReady) setTimeout(() => cb(WA), 0)
      else readyQueue.push(cb)
    },

    on: on,
    off: off,

    cart: {
      set: cartSet,
      add: function(itemId, delta, opts) {
        if (delta === undefined || delta === null) delta = 1
        const current = (cd.data && cd.data.items) ? cd.inCart(itemId) : 0
        return cartSet(itemId, current + delta, opts)
      },
      remove: function(itemId) {
        return cartSet(itemId, 0)
      },
      clear: function() {
        if (!cd.data || !(cd.data.items || []).length) return Promise.resolve(cartSnapshot())
        const promise = nextCartChange()
        try {
          cd.clear()
        } catch (e) {
          return Promise.reject(e)
        }
        return promise
      },
      get: cartSnapshot,
      repeat: notSupported('cart.repeat')
    },

    ui: {
      openLogin: function() {
        const login = singletons['login']
        if (!login) {
          console.warn('WA.ui.openLogin: login dialog is not present on this page')
          return
        }
        // LoginButton#open toggles — do not close an already opened dialog
        if (login.state && login.state.openedLogin) return
        login.open()
      },
      closeLogin: function() {
        const login = singletons['login']
        if (login && login.close) login.close()
      },
      openCityDialog: function() {
        if (typeof sharedData.toggleCityBranchDialogOpened !== 'function') {
          console.warn('WA.ui.openCityDialog is not supported in this theme')
          return
        }
        if (sharedData.chooseCityBranchDialogOpened) return
        sharedData.toggleCityBranchDialogOpened()
      },
      setCity: notSupported('ui.setCity'),
      openMobileMenu: function() { mobileMenuSet(true) },
      closeMobileMenu: function() { mobileMenuSet(false) },
      toggleMobileMenu: function() { mobileMenuToggle() },
      mount: notSupported('ui.mount')
    },

    site: {
      // same shape as newgen: flat site_params + companyId (here from the
      // data-shared blob — legacy themes have no body[data-company-id])
      params: function() {
        const data = sharedData.data || {}
        return Object.assign({}, data.site_params, { companyId: data.company_id || null })
      }
    },

    theme: {
      // site_design_type of the company, same source as the newgen WA
      // (body classes carry styling flags, not the theme name)
      get name() {
        const data = sharedData.data || {}
        return (data.site_params && data.site_params.design) || null
      },
      setStyle: setStyle,
      removeStyle: removeStyle,
      setCatalogCols: setCatalogCols
    },

    catalog: {
      get: notSupported('catalog.get'),
      item: notSupported('catalog.item'),
      search: notSupported('catalog.search')
    },

    user: {
      get: notSupported('user.get'),
      logout: notSupported('user.logout')
    },

    http: {
      get: notSupported('http.get'),
      post: notSupported('http.post'),
      put: notSupported('http.put'),
      del: notSupported('http.del')
    }
  }

  cd.subject.subscribe(() => {
    const snapshot = cartSnapshot()
    emit('cart:changed', snapshot)
    documentEvent('wa:cart:changed', snapshot)
  })

  window.WA = WA

  // gtag-style queue: MCP-rendered <script> runs before this pack loads and
  // pushes callbacks into window.WAQ
  const queue = (window.WAQ = window.WAQ || [])
  while (queue.length) {
    const cb = queue.shift()
    try {
      if (typeof cb === 'function') cb(WA)
    } catch (e) {
      console.error('WAQ callback failed', e)
    }
  }
  queue.push = function(cb) {
    try {
      if (typeof cb === 'function') cb(WA)
    } catch (e) {
      console.error('WAQ callback failed', e)
    }
    return queue.length // Array#push contract (queue stays empty after drain)
  }

  function markReady() {
    if (isReady) return
    isReady = true
    while (readyQueue.length) {
      const cb = readyQueue.shift()
      try { cb(WA) } catch (e) { console.error('WA.ready callback failed', e) }
    }
    emit('ready', WA)
    documentEvent('wa:ready', { WA: WA })
  }

  return { WA: WA, markReady: markReady }
}



// WEBPACK FOOTER //
// ./app/javascript/website_default/js/wa/index.js